/**
 * Heat — keep cursor movement balanced: too fast overheats, too still freezes.
 * Right-edge bar plus cursor tint: blue (cold) → white (center) → red (hot).
 */

export const HEAT_BALLS_START = 50;
export const HEAT_SCORE_PER_SPAWN = 500;
export const HEAT_BALLS_PER_SPAWN = 50;
export const HEAT_BALL_SPEED = 0.8;

/** 0 = cold (bottom), 0.5 = safe center, 1 = hot (top). */
let heatLevel = 0.5;
let heatRiseRate = 0.00275;
let heatCoolRate = 0.0042;

export function setHeatTuning(opts?: { heatRiseRate?: number; heatCoolRate?: number }): void {
    heatRiseRate =
        opts?.heatRiseRate != null && Number.isFinite(opts.heatRiseRate)
            ? Math.max(0, opts.heatRiseRate)
            : 0.00275;
    heatCoolRate =
        opts?.heatCoolRate != null && Number.isFinite(opts.heatCoolRate)
            ? Math.max(0, opts.heatCoolRate)
            : 0.0042;
}

export function resetHeatState(): void {
    heatLevel = 0.5;
}

export function getHeatLevel(): number {
    return heatLevel;
}

export type HeatDeathReason = "hot" | "cold";

export function getHeatDeathMessage(reason: HeatDeathReason): string {
    return reason === "hot" ? "Excess heat!" : "Excess cold!";
}

/**
 * Update heat from in-game cursor movement. Returns a death reason at extremes.
 */
export function tickHeat(
    prevX: number,
    prevY: number,
    curX: number,
    curY: number,
    dt: number,
    layoutScale: number,
): HeatDeathReason | null {
    const fs = dt * 60;
    const dist = Math.hypot(curX - prevX, curY - prevY);
    const idleThreshold = Math.max(0.2, 0.28 * layoutScale) * fs;

    if (dist > idleThreshold) {
        const rise = (dist / Math.max(layoutScale, 0.5)) * heatRiseRate * fs;
        heatLevel += rise;
    } else {
        heatLevel -= heatCoolRate * fs;
    }

    if (heatLevel >= 1) {
        heatLevel = 1;
        return "hot";
    }
    if (heatLevel <= 0) {
        heatLevel = 0;
        return "cold";
    }
    return null;
}

function lerpByte(a: number, b: number, t: number): number {
    return Math.round(a + (b - a) * t);
}

function heatRgbToHex(r: number, g: number, b: number): string {
    const h = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
    return `#${h(r)}${h(g)}${h(b)}`;
}

/** Solid cursor fill: cold blue at 0, white at 0.5, hot red at 1. Hex so cosmetic tint parsers work. */
export function getHeatCursorColor(level: number): string {
    const clamped = Math.max(0, Math.min(1, level));
    const cold = { r: 51, g: 136, b: 255 };
    const mid = { r: 255, g: 255, b: 255 };
    const hot = { r: 255, g: 51, b: 51 };

    if (clamped <= 0.5) {
        const t = clamped / 0.5;
        return heatRgbToHex(
            lerpByte(cold.r, mid.r, t),
            lerpByte(cold.g, mid.g, t),
            lerpByte(cold.b, mid.b, t),
        );
    }

    const t = (clamped - 0.5) / 0.5;
    return heatRgbToHex(
        lerpByte(mid.r, hot.r, t),
        lerpByte(mid.g, hot.g, t),
        lerpByte(mid.b, hot.b, t),
    );
}

/** Vertical heat bar on the right edge of the play area. */
export function drawHeatBar(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    layoutScale: number,
    level: number,
): void {
    if (w < 24 || h < 24) return;

    const barW = Math.max(14, 16 * layoutScale);
    const margin = Math.max(12, 14 * layoutScale);
    const barH = Math.min(h * 0.58, h - margin * 2);
    const barX = w - margin - barW;
    const barY = (h - barH) * 0.5;
    const r = Math.min(barW * 0.35, 6 * layoutScale);

    ctx.save();

    ctx.beginPath();
    ctx.roundRect(barX, barY, barW, barH, r);
    ctx.clip();

    const grad = ctx.createLinearGradient(0, barY, 0, barY + barH);
    grad.addColorStop(0, "#ff3333");
    grad.addColorStop(0.38, "#ff4444");
    grad.addColorStop(0.5, "#c8b8d8");
    grad.addColorStop(0.62, "#5599ee");
    grad.addColorStop(1, "#3388ff");
    ctx.fillStyle = grad;
    ctx.fillRect(barX, barY, barW, barH);

    const safeH = Math.max(4, barH * 0.08);
    const safeY = barY + barH * 0.5 - safeH * 0.5;
    ctx.fillStyle = "rgba(255,255,255,0.16)";
    ctx.fillRect(barX, safeY, barW, safeH);

    ctx.restore();

    ctx.save();
    ctx.strokeStyle = "rgba(255,255,255,0.72)";
    ctx.lineWidth = Math.max(1.5, 2 * layoutScale);
    ctx.beginPath();
    ctx.roundRect(barX, barY, barW, barH, r);
    ctx.stroke();

    const clamped = Math.max(0, Math.min(1, level));
    const indicatorY = barY + barH * (1 - clamped);
    const indicatorPad = Math.max(2, 3 * layoutScale);

    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = Math.max(2.5, 3 * layoutScale);
    ctx.shadowColor = "rgba(255,255,255,0.85)";
    ctx.shadowBlur = 8 * layoutScale;
    ctx.beginPath();
    ctx.moveTo(barX + indicatorPad, indicatorY);
    ctx.lineTo(barX + barW - indicatorPad, indicatorY);
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(barX - Math.max(4, 5 * layoutScale), indicatorY, Math.max(3, 3.5 * layoutScale), 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
}
